<template>
  <aside :class="sidebarClass">
    <div :class="headerClass">
      <div :class="logoClass" @click="goHome">
        <div class="w-10 h-10 bg-gradient-to-br from-[#F2C94C] to-[#E5A73B] rounded-xl flex-shrink-0 flex items-center justify-center shadow-sm border border-[#F2C94C]/30">
          <svg class="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 2a7 7 0 0 1 7 7c0 2.38-1.19 4.47-3 5.74V17a2 2 0 0 1-2 2H10a2 2 0 0 1-2-2v-2.26C6.19 13.47 5 11.38 5 9a7 7 0 0 1 7-7z"/>
            <path d="M9 21h6"/>
            <circle cx="12" cy="9" r="2"/>
            <path d="M12 2v2"/>
            <path d="M5 9H3"/>
            <path d="M21 9h-2"/>
          </svg>
        </div>
        <h1 v-if="!isCollapsed" class="text-lg font-bold tracking-wider whitespace-nowrap text-slate-900">AI 管理能力提升助手</h1>
      </div>
      <button v-if="!isCollapsed" @click="$emit('update:isOpen', false)" class="md:hidden">
        <IconX class="w-6 h-6 text-slate-400" />
      </button>
    </div>

    <nav class="flex-1 px-2 py-6 space-y-2 overflow-y-auto no-scrollbar">
      <button
        v-for="item in menuItems"
        :key="item.id"
        @click="navigate(item)"
        :class="menuItemClass(item.id)"
        :title="isCollapsed ? item.label : ''"
      >
        <component :is="item.icon" class="w-6 h-6 flex-shrink-0" />
        <div v-if="!isCollapsed" class="ml-4 text-left overflow-hidden">
          <div class="text-sm whitespace-nowrap">{{ item.label }}</div>
          <div class="text-xs text-slate-500 font-normal whitespace-nowrap">{{ item.subLabel }}</div>
        </div>
      </button>
    </nav>

    <div class="p-2 border-t border-slate-100">
      <button
        @click="goProfile"
        :class="profileClass"
      >
        <div class="w-8 h-8 rounded-lg bg-[#F2C94C]/10 flex-shrink-0 flex items-center justify-center border border-[#F2C94C]/20">
          <IconUsers class="w-5 h-5 text-[#F2C94C]" />
        </div>
        <template v-if="!isCollapsed">
          <div class="ml-3 overflow-hidden flex-1 text-left">
            <div class="text-xs font-bold whitespace-nowrap text-slate-900">{{ userStats.points }} 积分</div>
          </div>
          <IconChevronRight class="w-4 h-4 text-slate-400" />
        </template>
      </button>
    </div>
  </aside>
</template>

<script>
import { IconBookOpen, IconTarget, IconActivity, IconHistory, IconUsers, IconChevronRight, IconX } from '../icons'

export default {
  name: 'AppSidebar',
  components: {
    IconBookOpen,
    IconTarget,
    IconActivity,
    IconHistory,
    IconUsers,
    IconChevronRight,
    IconX
  },
  props: {
    activeView: {
      type: String,
      default: 'home'
    },
    context: {
      type: Object,
      default: () => ({})
    },
    userStats: {
      type: Object,
      default: () => ({ points: 0 })
    },
    isOpen: {
      type: Boolean,
      default: false
    },
    isCollapsed: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      menuItems: [
        { id: 'home', icon: 'IconBookOpen', label: '问一问', subLabel: '学习标杆实践', path: '/' },
        { id: 'practice', icon: 'IconTarget', label: '练一练', subLabel: '情景模拟练习', path: '/practice' },
        { id: 'diagnose-start', icon: 'IconActivity', label: '聊一聊', subLabel: '深度智能诊断', path: '/diagnose-start' },
        { id: 'history', icon: 'IconHistory', label: '历史记录', subLabel: '我的过往操作', path: '/history' },
      ]
    }
  },
  computed: {
    sidebarClass() {
      return [
        'fixed md:static inset-y-0 left-0 z-50 transition-all duration-300 bg-white text-slate-900 flex flex-col border-r border-slate-200 overflow-hidden',
        this.isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
        this.isCollapsed ? 'w-20' : 'w-64 lg:w-72'
      ].join(' ')
    },
    headerClass() {
      return [
        'p-4 flex items-center border-b border-slate-100 h-16',
        this.isCollapsed ? 'justify-center' : 'justify-between'
      ].join(' ')
    },
    logoClass() {
      return [
        'flex items-center cursor-pointer overflow-hidden',
        this.isCollapsed ? 'justify-center w-full' : 'space-x-3'
      ].join(' ')
    },
    profileClass() {
      return [
        'w-full flex items-center p-2 rounded-xl bg-slate-50 border border-slate-100 hover:bg-slate-100 transition-all',
        this.isCollapsed ? 'justify-center' : ''
      ].join(' ')
    }
  },
  methods: {
    menuItemClass(itemId) {
      const base = 'w-full flex items-center p-3 rounded-xl transition-all'
      const active = this.activeView === itemId
        ? 'bg-[#F2C94C] text-white font-bold shadow-md'
        : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'
      const collapsed = this.isCollapsed ? 'justify-center' : ''
      return [base, active, collapsed].join(' ')
    },
    goHome() {
      this.$router.push('/')
      this.$emit('update:isBriefingMode', false)
      this.$emit('navigate', 'home')
    },
    navigate(item) {
      this.$router.push(item.path)
      this.$emit('update:isBriefingMode', false)
      this.$emit('navigate', item.id)
      this.$emit('update:isOpen', false)
    },
    goProfile() {
      this.$router.push('/profile')
      this.$emit('update:isBriefingMode', false)
      this.$emit('navigate', 'profile')
      this.$emit('update:isOpen', false)
    }
  }
}
</script>
